'use client';

import { formatReadableNumber } from '@lfg/core/lib';
import { Modal, type ModalHandle, type ModalProps } from '@lfg/ui/components';
import { ArrowForward } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { icons } from '@/assets/icons';
import { dividers } from '@/assets/materials';
import type { NodeSchemaType } from '@/global/schemas/node.schema';

import Button from './Button';
import { CoinIcon } from './CoinIcon';

export interface NodeDetailModalHandle extends ModalHandle {}
export interface NodeDetailModalProps extends Omit<ModalProps, 'children'> {
  item: NodeSchemaType;
  setSelectedNode: React.Dispatch<React.SetStateAction<NodeSchemaType | null>>;
}

const NodeDetailModal = forwardRef<NodeDetailModalHandle, NodeDetailModalProps>(
  ({ item, setSelectedNode, ...rest }, ref) => {
    const theme = useTheme();
    const modalRef = useRef<ModalHandle>(null);

    const show = useCallback(() => {
      modalRef.current?.show();
    }, [modalRef.current]);

    const hide = useCallback(() => {
      modalRef.current?.hide();
      setSelectedNode(null);
    }, [modalRef.current]);

    useImperativeHandle(ref, () => ({ show, hide }), [show, hide]);

    const openNodeSaleUrl = () => {
      window.open(item.nodeSaleUrl, '_blank');
    };

    return (
      <Modal
        ref={modalRef}
        bgColor={'#060912'}
        padding={'32px 24px 24px'}
        borderRadius={'24px'}
        width={'800px'}
        sx={{
          maxHeight: { xs: '95vh', sm: 'unset' },
          overflowY: { xs: 'auto', sm: 'unset' },
          border: '1px solid rgba(255,255,255,0.1)',
        }}
        {...rest}>
        <Stack>
          <Box display={'flex'} justifyContent={'flex-end'}>
            <IconButton onClick={hide} sx={{ padding: 0 }}>
              <Image width={24} height={24} src={icons.ic_x} alt='' />
            </IconButton>
          </Box>
          <Stack
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            direction={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 2, sm: 3 }}
            paddingTop={'10px'}
            flexGrow={1}
            position={'relative'}>
            <Stack
              height={{ xs: '240px', sm: '300px' }}
              width={{ xs: '240px', sm: '300px' }}
              position='relative'
              overflow={'hidden'}
              sx={{ borderRadius: '8px' }}>
              <Image
                src={item.asset}
                alt={item.name}
                fill
                sizes='(max-width: 600px) 240px, 300px'
                style={{
                  objectFit: 'contain',
                  objectPosition: 'top',
                }}
                priority
              />
            </Stack>
            <Stack width={{ xs: '100%', sm: 'calc(100% - 300px)' }} gap={{ xs: 2, sm: 3 }}>
              <Stack gap={{ xs: 2, sm: 3 }}>
                <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} gap={{ xs: 2, sm: 3 }}>
                  <Typography
                    color={'white'}
                    fontSize={{ xs: '20px', sm: '32px' }}
                    fontWeight={600}
                    lineHeight={'1.5em'}>
                    {item.name}
                  </Typography>
                </Stack>
                <Stack alignItems={'center'} gap={1} direction={'row'}>
                  <Image
                    src={'/icons/coins/ethereum.svg'}
                    alt={'highlight'}
                    width={15}
                    height={24}
                    className={'w-auto h-6 object-contain'}
                  />
                  <Typography
                    variant={'bodyMedium'}
                    color={'white'}
                    className={'mix-blend'}
                    fontSize={{ xs: '12px', sm: '16px' }}>
                    {item.nftType ?? 'ERC-1155'} NFT
                  </Typography>
                </Stack>
                <Typography
                  fontSize={{ xs: '14px', md: '16px' }}
                  variant={'bodyLarge'}
                  color={'#FFFFFF'}
                  className={'mix-blend'}
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                  {item.description}
                </Typography>
              </Stack>

              {/* Glass tier/price panel */}
              <Stack
                direction={'row'}
                alignItems={'center'}
                gap={2}
                padding={'24px'}
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: '16px',
                  backdropFilter: 'blur(15px)',
                }}>
                <Stack gap={1.5} flex={1}>
                  <Typography color={'rgba(255, 255, 255, 0.6)'} fontSize={'16px'} fontWeight={400}>
                    Current Tier
                  </Typography>
                  <Typography color={'#FFFFFF'} fontSize={'24px'} fontWeight={500}>
                    TIER {item.currentTier}
                  </Typography>
                </Stack>
                <Stack
                  gap={1.5}
                  flex={1}
                  sx={{ borderLeft: '1px solid rgba(255, 255, 255, 0.09)', paddingLeft: '24px' }}>
                  <Typography color={'rgba(255, 255, 255, 0.6)'} fontSize={'16px'} fontWeight={400}>
                    Price
                  </Typography>
                  <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <Typography color={'#FFFFFF'} fontSize={'24px'} fontWeight={500}>
                      {formatReadableNumber(item.price, {
                        minimumFractionDigits: 2,
                        style: 'decimal',
                        preventRounding: true,
                      })}
                    </Typography>
                    <Typography color={'#FFFFFF'} fontSize={'24px'} fontWeight={300} sx={{ opacity: 0.5 }}>
                      {(item.token || 'USDC').toUpperCase()}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  width={48}
                  height={48}
                  alignItems={'center'}
                  justifyContent={'center'}
                  sx={{ background: '#890096', borderRadius: '100px', cursor: 'pointer' }}
                  onClick={openNodeSaleUrl}>
                  <ArrowForward />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    );
  },
);

export default NodeDetailModal;
